import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LandingSEO } from '../components/SEO';
import LanguageDropdown from '../components/LanguageDropdown';
import { 
  QuickSetupIcon, 
  MultiLanguageIcon, 
  CustomDesignIcon, 
  SmartStatsIcon,
  StepNumber,
  FloatingShapes 
} from '../components/icons/FeatureIcons';
import axios from 'axios';
import { Clock, Shield, Zap, Globe, Headphones as HeadphonesIcon, Puzzle, ArrowRight, Upload, Download, Link2 } from 'lucide-react';

import { AuthContext } from '../App';

const baseAPI = process.env.REACT_APP_API_URL || '/api';

export default function Landing() {
  const { t, i18n } = useTranslation();
  const { token } = useContext(AuthContext);
  const [pricingConfig, setPricingConfig] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${baseAPI}/public/pricing`);
        setPricingConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  const isComingSoon = (planId) => {
    return pricingConfig?.plans?.[planId]?.coming_soon === true;
  };

  const getComingSoonText = (planId) => {
    const plan = pricingConfig?.plans?.[planId];
    if (i18n.language === 'en') {
      return plan?.coming_soon_text_en || 'Coming Soon';
    }
    return plan?.coming_soon_text || 'Yakında';
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { labelKey: 'landing.nav.features', fallback: 'Özellikler', id: 'features' },
    { labelKey: 'landing.nav.howItWorks', fallback: 'Nasıl Çalışır', id: 'how-it-works' },
    { labelKey: 'landing.nav.whoFor', fallback: 'Kimin İçin', id: 'who-for' },
    { labelKey: 'landing.nav.pricing', fallback: 'Fiyatlandırma', id: 'pricing' },
    { labelKey: 'landing.nav.addons', fallback: 'Add-ons', id: 'addons' },
    { labelKey: 'landing.nav.dashboard', fallback: 'Panel', id: 'dashboard' },
    { labelKey: 'landing.nav.testimonials', fallback: 'Yorumlar', id: 'testimonials' }
  ];

  return (
    <>
    <LandingSEO />
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
          <a href="/">
            <img src="/idemenu-logo.svg" alt="idemenu" className="h-10 w-auto flex-shrink-0 cursor-pointer" />
          </a>

          <nav className="hidden lg:flex items-center gap-6 text-base font-semibold text-gray-700">
            {navLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => scrollToSection(link.id)}
                className="px-3 py-2 rounded-full bg-transparent text-gray-700 hover:text-purple-700 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-200 transition-colors"
              >
                {t(link.labelKey, link.fallback)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <LanguageDropdown />
            <button
              onClick={() => window.location.href = token ? '/admin' : '/login'}
              className="px-6 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg font-medium text-purple-700 hover:bg-indigo-100 transition-colors"
            >
              {token
                ? t('footer.admin', i18n.language === 'en' ? 'Admin' : 'Yönetici')
                : t('footer.login', i18n.language === 'en' ? 'Login' : 'Giriş Yap')}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-white space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                {t('landing.hero.title')}<br />
                {t('landing.hero.subtitle')}
              </h1>
              
              <p className="text-xl sm:text-2xl text-purple-100 leading-relaxed">
                {t('landing.hero.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = '/signup'}
                  className="px-8 py-4 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-all transform hover:scale-105 shadow-lg"
                >
                  {t('landing.hero.cta')}
                </button>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="px-8 py-4 bg-purple-500/30 text-white font-semibold rounded-lg hover:bg-purple-500/40 transition-all border-2 border-white/30"
                >
                  {t('landing.hero.demo')}
                </button>
              </div>
              
              <p className="text-sm text-purple-200">
                {t('landing.hero.noCard')}
              </p>
            </div>
            
            {/* Right - Visual */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                  {/* Phone mockup with inline SVG illustration */}
                  <div className="relative w-80 h-[650px] transform rotate-3 hover:rotate-0 transition-all duration-500">
                    {/* Phone frame */}
                    <div className="absolute inset-0 bg-gray-900 rounded-[3.5rem] shadow-2xl">
                      {/* Notch */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-7 bg-gray-900 rounded-b-3xl z-10"></div>
                    
                      {/* Screen with menu illustration */}
                      <div className="absolute inset-4 bg-white rounded-[2.8rem] overflow-hidden">
                        {/* App Header */}
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 pt-8">
                          <div className="flex items-center justify-between">
                            <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                            <div className="text-white font-bold text-lg">Bella Cafe</div>
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                              <div className="w-4 h-4 border-2 border-white rounded"></div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Category Pills */}
                        <div className="flex gap-2 p-4 overflow-hidden">
                          <div className="px-4 py-2 bg-purple-600 text-white rounded-full text-sm font-medium whitespace-nowrap">☕ Kahveler</div>
                          <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm whitespace-nowrap">🥐 Tatlılar</div>
                          <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm whitespace-nowrap">🥗 Yemek</div>
                        </div>
                        
                        {/* Menu Items */}
                        <div className="px-4 space-y-3">
                          {/* Item 1 */}
                          <div className="flex items-center bg-gray-50 rounded-xl p-3 gap-3">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-200 to-amber-400 rounded-lg flex items-center justify-center text-2xl">☕</div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">Latte</div>
                              <div className="text-sm text-gray-500">Sütlü kahve</div>
                              <div className="text-purple-600 font-bold mt-1">₺45</div>
                            </div>
                          </div>
                          
                          {/* Item 2 */}
                          <div className="flex items-center bg-gray-50 rounded-xl p-3 gap-3">
                            <div className="w-16 h-16 bg-gradient-to-br from-orange-200 to-orange-400 rounded-lg flex items-center justify-center text-2xl">🥐</div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">Kruvasan</div>
                              <div className="text-sm text-gray-500">Tereyağlı</div>
                              <div className="text-purple-600 font-bold mt-1">₺35</div>
                            </div>
                          </div>
                          
                          {/* Item 3 */}
                          <div className="flex items-center bg-gray-50 rounded-xl p-3 gap-3">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-200 to-green-400 rounded-lg flex items-center justify-center text-2xl">🥗</div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">Salata</div>
                              <div className="text-sm text-gray-500">Akdeniz</div>
                              <div className="text-purple-600 font-bold mt-1">₺65</div>
                            </div>
                          </div>
                          
                          {/* Item 4 */}
                          <div className="flex items-center bg-gray-50 rounded-xl p-3 gap-3">
                            <div className="w-16 h-16 bg-gradient-to-br from-pink-200 to-pink-400 rounded-lg flex items-center justify-center text-2xl">🍰</div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">Cheesecake</div>
                              <div className="text-sm text-gray-500">Frambuazlı</div>
                              <div className="text-purple-600 font-bold mt-1">₺55</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Bottom Nav */}
                        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-around rounded-b-[2.5rem]">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <div className="w-5 h-5 bg-purple-600 rounded-sm"></div>
                          </div>
                          <div className="w-10 h-10 bg-gray-100 rounded-full"></div>
                          <div className="w-10 h-10 bg-gray-100 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                
                  {/* Floating QR Code - SVG */}
                  <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white rounded-2xl shadow-2xl p-3 transform hover:scale-110 transition-transform">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      {/* QR Code Pattern */}
                      <rect x="0" y="0" width="100" height="100" fill="white"/>
                      {/* Position Detection Patterns */}
                      <rect x="5" y="5" width="25" height="25" fill="#1a1a1a"/>
                      <rect x="10" y="10" width="15" height="15" fill="white"/>
                      <rect x="13" y="13" width="9" height="9" fill="#1a1a1a"/>
                      
                      <rect x="70" y="5" width="25" height="25" fill="#1a1a1a"/>
                      <rect x="75" y="10" width="15" height="15" fill="white"/>
                      <rect x="78" y="13" width="9" height="9" fill="#1a1a1a"/>
                      
                      <rect x="5" y="70" width="25" height="25" fill="#1a1a1a"/>
                      <rect x="10" y="75" width="15" height="15" fill="white"/>
                      <rect x="13" y="78" width="9" height="9" fill="#1a1a1a"/>
                      
                      {/* Data modules */}
                      <rect x="35" y="5" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="45" y="5" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="55" y="5" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="35" y="15" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="50" y="15" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="60" y="15" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="40" y="25" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="55" y="25" width="5" height="5" fill="#1a1a1a"/>
                      
                      <rect x="5" y="35" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="15" y="35" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="25" y="35" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="5" y="45" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="20" y="45" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="5" y="55" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="15" y="55" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="25" y="55" width="5" height="5" fill="#1a1a1a"/>
                      
                      <rect x="35" y="35" width="30" height="30" rx="4" fill="#7c3aed"/>
                      <text x="50" y="55" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">ide</text>
                      
                      <rect x="70" y="35" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="80" y="35" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="90" y="35" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="70" y="45" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="85" y="45" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="70" y="55" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="80" y="55" width="5" height="5" fill="#1a1a1a"/>
                      
                      <rect x="35" y="70" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="45" y="70" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="55" y="70" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="40" y="80" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="50" y="80" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="60" y="80" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="35" y="90" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="50" y="90" width="5" height="5" fill="#1a1a1a"/>
                      
                      <rect x="70" y="70" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="80" y="70" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="90" y="70" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="75" y="80" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="85" y="80" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="70" y="90" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="80" y="90" width="5" height="5" fill="#1a1a1a"/>
                      <rect x="90" y="90" width="5" height="5" fill="#1a1a1a"/>
                    </svg>
                </div>
                
                  {/* Decorative elements */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full opacity-20 blur-2xl"></div>
                  <div className="absolute -bottom-4 -right-8 w-32 h-32 bg-purple-400 rounded-full opacity-20 blur-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why idemenu Section */}
      <section id="features" className="py-20 lg:py-32 bg-gray-50 relative overflow-hidden">
        <FloatingShapes />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                Icon: QuickSetupIcon,
                title: t('landing.features.quickSetup'),
                text: t('landing.features.quickSetupDesc')
              },
              {
                Icon: MultiLanguageIcon,
                title: t('landing.features.multiLanguage'),
                text: t('landing.features.multiLanguageDesc')
              },
              {
                Icon: CustomDesignIcon,
                title: t('landing.features.customDesign'),
                text: t('landing.features.customDesignDesc')
              },
              {
                Icon: SmartStatsIcon,
                title: t('landing.features.smartStats'),
                text: t('landing.features.smartStatsDesc')
              }
            ].map((feature, idx) => (
              <div key={idx} className="group bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  <feature.Icon />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.howItWorks.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            {/* Step 1 - Create Menu */}
            <div className="text-center group">
              <div className="mb-6 relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-2xl transition-shadow bg-gradient-to-br from-purple-50 to-indigo-100 p-6 h-64 flex items-center justify-center">
                <svg viewBox="0 0 200 160" className="w-full h-full max-w-xs">
                  {/* Browser Window */}
                  <rect x="10" y="10" width="180" height="140" rx="8" fill="white" stroke="#e5e7eb" strokeWidth="2"/>
                  <rect x="10" y="10" width="180" height="24" rx="8" fill="#f3f4f6"/>
                  <circle cx="26" cy="22" r="4" fill="#ef4444"/>
                  <circle cx="38" cy="22" r="4" fill="#fbbf24"/>
                  <circle cx="50" cy="22" r="4" fill="#22c55e"/>
                  <rect x="70" y="18" width="80" height="8" rx="4" fill="#e5e7eb"/>
                  
                  {/* Dashboard Content */}
                  <rect x="20" y="44" width="60" height="8" rx="2" fill="#7c3aed"/>
                  <text x="20" y="51" fill="white" fontSize="6" fontWeight="bold">Menü</text>
                  
                  {/* Categories */}
                  <rect x="20" y="58" width="35" height="16" rx="4" fill="#7c3aed"/>
                  <rect x="58" y="58" width="35" height="16" rx="4" fill="#e5e7eb"/>
                  <rect x="96" y="58" width="35" height="16" rx="4" fill="#e5e7eb"/>
                  
                  {/* Menu Items */}
                  <rect x="20" y="80" width="80" height="24" rx="4" fill="#faf5ff" stroke="#e9d5ff" strokeWidth="1"/>
                  <rect x="26" y="86" width="12" height="12" rx="2" fill="#c4b5fd"/>
                  <rect x="42" y="88" width="40" height="4" rx="1" fill="#7c3aed"/>
                  <rect x="42" y="94" width="25" height="3" rx="1" fill="#a78bfa"/>
                  
                  <rect x="20" y="108" width="80" height="24" rx="4" fill="#faf5ff" stroke="#e9d5ff" strokeWidth="1"/>
                  <rect x="26" y="114" width="12" height="12" rx="2" fill="#c4b5fd"/>
                  <rect x="42" y="116" width="35" height="4" rx="1" fill="#7c3aed"/>
                  <rect x="42" y="122" width="28" height="3" rx="1" fill="#a78bfa"/>
                  
                  {/* Add Button */}
                  <circle cx="160" cy="120" r="16" fill="#7c3aed"/>
                  <rect x="154" y="118" width="12" height="4" rx="1" fill="white"/>
                  <rect x="158" y="114" width="4" height="12" rx="1" fill="white"/>
                  
                  {/* Cursor */}
                  <polygon points="140,95 155,110 148,112 145,122 142,121 145,111 138,108" fill="#1a1a1a"/>
                </svg>
              </div>
              <div className="mt-6">
                <StepNumber number={1} color="purple" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-4">{t('landing.howItWorks.step1Title')}</h3>
              <p className="text-lg text-gray-600">{t('landing.howItWorks.step1Desc')}</p>
            </div>
            
            {/* Step 2 - Share QR */}
            <div className="text-center group">
              <div className="mb-6 relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-2xl transition-shadow bg-gradient-to-br from-amber-50 to-orange-100 p-6 h-64 flex items-center justify-center">
                <svg viewBox="0 0 200 160" className="w-full h-full max-w-xs">
                  {/* Table */}
                  <ellipse cx="100" cy="140" rx="80" ry="12" fill="#d4a373"/>
                  <rect x="30" y="80" width="140" height="60" rx="4" fill="#e9c46a"/>
                  
                  {/* Table Items */}
                  <ellipse cx="50" cy="90" rx="15" ry="8" fill="white" opacity="0.6"/>
                  <ellipse cx="150" cy="90" rx="15" ry="8" fill="white" opacity="0.6"/>
                  
                  {/* QR Stand */}
                  <rect x="80" y="60" width="40" height="50" rx="4" fill="white" stroke="#e5e7eb" strokeWidth="2"/>
                  <rect x="85" y="65" width="30" height="30" rx="2" fill="#1a1a1a"/>
                  {/* QR Pattern */}
                  <rect x="87" y="67" width="8" height="8" fill="white"/>
                  <rect x="97" y="67" width="8" height="8" fill="white"/>
                  <rect x="107" y="67" width="8" height="8" fill="white"/>
                  <rect x="87" y="77" width="8" height="8" fill="white"/>
                  <rect x="97" y="77" width="8" height="8" fill="#7c3aed"/>
                  <rect x="107" y="77" width="8" height="8" fill="white"/>
                  <rect x="87" y="87" width="8" height="8" fill="white"/>
                  <rect x="97" y="87" width="8" height="8" fill="white"/>
                  <rect x="107" y="87" width="8" height="8" fill="white"/>
                  <text x="100" y="103" textAnchor="middle" fill="#7c3aed" fontSize="5" fontWeight="bold">Menü</text>
                  
                  {/* Phone Scanning */}
                  <rect x="130" y="25" width="45" height="75" rx="6" fill="#1a1a1a"/>
                  <rect x="134" y="32" width="37" height="60" rx="2" fill="white"/>
                  {/* Camera Focus */}
                  <rect x="140" y="45" width="25" height="25" fill="none" stroke="#7c3aed" strokeWidth="2" strokeDasharray="6 4"/>
                  {/* Scan Lines */}
                  <line x1="135" y1="58" x2="170" y2="58" stroke="#22c55e" strokeWidth="2" opacity="0.8">
                    <animate attributeName="y1" values="45;70;45" dur="1.5s" repeatCount="indefinite"/>
                    <animate attributeName="y2" values="45;70;45" dur="1.5s" repeatCount="indefinite"/>
                  </line>
                  
                  {/* Hand */}
                  <ellipse cx="152" cy="110" rx="20" ry="12" fill="#fcd5ce"/>
                </svg>
              </div>
              <div className="mt-6">
                <StepNumber number={2} color="amber" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-4">{t('landing.howItWorks.step2Title')}</h3>
              <p className="text-lg text-gray-600">{t('landing.howItWorks.step2Desc')}</p>
            </div>
            
            {/* Step 3 - Instant Updates */}
            <div className="text-center group">
              <div className="mb-6 relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-2xl transition-shadow bg-gradient-to-br from-green-50 to-emerald-100 p-6 h-64 flex items-center justify-center">
                <svg viewBox="0 0 200 160" className="w-full h-full max-w-xs">
                  {/* Phone */}
                  <rect x="60" y="10" width="80" height="140" rx="12" fill="#1a1a1a"/>
                  <rect x="66" y="20" width="68" height="120" rx="4" fill="white"/>
                  
                  {/* App Header */}
                  <rect x="66" y="20" width="68" height="20" fill="#7c3aed"/>
                  <text x="100" y="33" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">Bella Cafe</text>
                  
                  {/* Menu Item with Update */}
                  <rect x="72" y="46" width="56" height="28" rx="4" fill="#f0fdf4" stroke="#22c55e" strokeWidth="2"/>
                  <rect x="76" y="50" width="14" height="14" rx="2" fill="#bbf7d0"/>
                  <rect x="94" y="52" width="28" height="4" rx="1" fill="#166534"/>
                  <rect x="94" y="58" width="18" height="3" rx="1" fill="#22c55e"/>
                  {/* NEW Badge */}
                  <rect x="108" y="64" width="16" height="8" rx="2" fill="#22c55e"/>
                  <text x="116" y="70" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">NEW</text>
                  
                  {/* Other Items */}
                  <rect x="72" y="78" width="56" height="22" rx="4" fill="#faf5ff"/>
                  <rect x="76" y="82" width="10" height="10" rx="2" fill="#e9d5ff"/>
                  <rect x="90" y="84" width="30" height="3" rx="1" fill="#7c3aed"/>
                  <rect x="90" y="89" width="20" height="3" rx="1" fill="#a78bfa"/>
                  
                  <rect x="72" y="104" width="56" height="22" rx="4" fill="#faf5ff"/>
                  <rect x="76" y="108" width="10" height="10" rx="2" fill="#e9d5ff"/>
                  <rect x="90" y="110" width="25" height="3" rx="1" fill="#7c3aed"/>
                  <rect x="90" y="115" width="18" height="3" rx="1" fill="#a78bfa"/>
                  
                  {/* Sync Indicator */}
                  <circle cx="160" cy="50" r="20" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
                  <path d="M150,50 L160,50 L160,42" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round"/>
                  <path d="M160,58 C165,58 170,53 170,48" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
                  <polygon points="172,50 170,46 168,50" fill="#22c55e"/>
                  
                  {/* Cloud Sync */}
                  <ellipse cx="35" cy="40" rx="18" ry="12" fill="#dbeafe"/>
                  <ellipse cx="28" cy="42" rx="10" ry="8" fill="#dbeafe"/>
                  <ellipse cx="42" cy="42" rx="10" ry="8" fill="#dbeafe"/>
                  {/* Arrow */}
                  <path d="M35,55 L35,70 M30,65 L35,70 L40,65" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="mt-6">
                <StepNumber number={3} color="green" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-4">{t('landing.howItWorks.step3Title')}</h3>
              <p className="text-lg text-gray-600">{t('landing.howItWorks.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is It For Section */}
      <section id="who-for" className="py-20 lg:py-32 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
              {t('landing.whoFor.title')}
            </h2>
            
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              {[
                  { icon: '☕', label: t('landing.whoFor.cafes'), image: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=500&h=400&fit=crop&q=80', alt: 'Cozy cafe interior with digital menu QR code on table' },
                  { icon: '🍽️', label: t('landing.whoFor.restaurants'), image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=500&h=400&fit=crop&q=80', alt: 'Fine dining restaurant with elegant table setting' },
                  { icon: '🏨', label: t('landing.whoFor.hotels'), image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=500&h=400&fit=crop&q=80', alt: 'Hotel restaurant and reception area' },
                  { icon: '🏖️', label: t('landing.whoFor.beach'), image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=500&h=400&fit=crop&q=80', alt: 'Beach club outdoor seating area with ocean view' },
                  { icon: '🍺', label: t('landing.whoFor.bars'), image: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=500&h=400&fit=crop&q=80', alt: 'Modern bar interior with ambient lighting' }
              ].map((item, idx) => (
                  <div key={idx} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                    <img 
                      src={item.image}
                      alt={item.alt}
                      className="w-full h-56 object-cover"
                      loading="lazy"
                    />
                  
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-6">
                      <div className="text-white">
                        <div className="text-3xl mb-2">{item.icon}</div>
                        <h3 className="text-xl font-bold">{item.label}</h3>
                      </div>
                    </div>
                </div>
              ))}
            </div>
            
            <p className="text-lg text-gray-600 italic">
              {t('landing.whoFor.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.pricing.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-purple-300 transition-colors">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('landing.pricing.free')}</h3>
              <ul className="space-y-4 mb-8">
                 <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.freeForSmall')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.freeFeature1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.freeFeature3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.freeFeature4')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.freeFeature5')}</span>
                </li>
              </ul>
              <button 
                onClick={() => window.location.href = '/signup'}
                className="w-full py-3 bg-gray-100 text-gray-900 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('landing.pricing.freeCta')}
              </button>
            </div>

            {/* Pro Plan - Highlighted */}
            <div className={`bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-2xl p-8 transform scale-105 shadow-2xl relative ${isComingSoon('pro') ? 'opacity-75' : ''}`}>
              {isComingSoon('pro') ? (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {getComingSoonText('pro')}
                </div>
              ) : (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                  {t('landing.pricing.proPopular')}
                </div>
              )}
              <h3 className="text-2xl font-bold mb-6 mt-2">{t('landing.pricing.pro')}</h3>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>{t('landing.pricing.proFeature1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>{t('landing.pricing.proFeature2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>{t('landing.pricing.proFeature3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>{t('landing.pricing.proFeature4')}</span>
                </li>
              </ul>
              <button 
                onClick={() => !isComingSoon('pro') && (window.location.href = '/signup')}
                disabled={isComingSoon('pro')}
                className={`w-full py-3 font-semibold rounded-lg transition-colors ${
                  isComingSoon('pro')
                    ? 'bg-white/50 text-purple-300 cursor-not-allowed'
                    : 'bg-white text-purple-700 hover:bg-purple-50'
                }`}
              >
                {isComingSoon('pro') ? getComingSoonText('pro') : t('landing.pricing.proCta')}
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className={`bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-purple-300 transition-colors relative ${isComingSoon('enterprise') ? 'opacity-75' : ''}`}>
              {isComingSoon('enterprise') && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {getComingSoonText('enterprise')}
                </div>
              )}
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('landing.pricing.enterprise')}</h3>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.enterpriseFeature1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.enterpriseFeature2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.enterpriseFeature3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.enterpriseFeature4')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">{t('landing.pricing.enterpriseFeature5')}</span>
                </li>
              </ul>
              <button 
                onClick={() => !isComingSoon('enterprise') && (window.location.href = '/contact')}
                disabled={isComingSoon('enterprise')}
                className={`w-full py-3 font-semibold rounded-lg transition-colors ${
                  isComingSoon('enterprise')
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {isComingSoon('enterprise') ? getComingSoonText('enterprise') : t('landing.pricing.enterpriseCta')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section id="addons" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-medium mb-6">
              <Puzzle size={16} />
              {t('landing.addons.badge', 'Pro & Enterprise Add-ons')}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.addons.title', 'Extend Your Menu Platform')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.addons.subtitle', 'Supercharge your restaurant operations with powerful add-ons. Import menus from your POS, export data, and integrate with external systems.')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Import/Export Add-on */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity" />
              <div className="relative bg-white border-2 border-purple-100 rounded-2xl p-8 hover:border-purple-300 transition-all hover:shadow-xl">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-6">
                  <Upload className="w-7 h-7 text-white" />
                </div>
                <div className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 mb-3">PRO</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('landing.addons.importExport.title', 'Import / Export')}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t('landing.addons.importExport.desc', 'Bulk import menus from CSV/Excel, export your data anytime, and sync with your POS via external identifiers.')}
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><Download size={14} className="text-purple-500" /> {t('landing.addons.importExport.f1', 'CSV & Excel import/export')}</li>
                  <li className="flex items-center gap-2"><Link2 size={14} className="text-purple-500" /> {t('landing.addons.importExport.f2', 'External ID for POS sync')}</li>
                  <li className="flex items-center gap-2"><Zap size={14} className="text-purple-500" /> {t('landing.addons.importExport.f3', 'Bulk update categories & items')}</li>
                </ul>
              </div>
            </div>

            {/* Coming Soon 1 */}
            <div className="relative bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center opacity-60">
              <div className="w-14 h-14 rounded-xl bg-gray-200 flex items-center justify-center mb-6">
                <Puzzle className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-500 mb-2">
                {t('landing.addons.comingSoon', 'Coming Soon')}
              </h3>
              <p className="text-sm text-gray-400">
                {t('landing.addons.comingSoonDesc', 'More powerful add-ons are on the way. Stay tuned!')}
              </p>
            </div>

            {/* Coming Soon 2 */}
            <div className="relative bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center opacity-60">
              <div className="w-14 h-14 rounded-xl bg-gray-200 flex items-center justify-center mb-6">
                <Puzzle className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-500 mb-2">
                {t('landing.addons.comingSoon', 'Coming Soon')}
              </h3>
              <p className="text-sm text-gray-400">
                {t('landing.addons.comingSoonDesc', 'More powerful add-ons are on the way. Stay tuned!')}
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a 
              href="/signup" 
              className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:text-purple-700 transition-colors group"
            >
              {t('landing.addons.cta', 'Get started and explore add-ons')}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

        {/* Dashboard Preview Section - NEW */}
        <section id="dashboard" className="py-20 lg:py-32 bg-gradient-to-br from-gray-900 to-purple-900 text-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                {t('landing.dashboard.title')}
              </h2>
              <p className="text-xl text-purple-200">
                {t('landing.dashboard.subtitle')}
              </p>
            </div>
          
            {/* Dashboard Illustration */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500 bg-white">
              <svg viewBox="0 0 1200 600" className="w-full h-auto">
                {/* Browser Chrome */}
                <rect x="0" y="0" width="1200" height="600" fill="white"/>
                <rect x="0" y="0" width="1200" height="40" fill="#f3f4f6"/>
                <circle cx="20" cy="20" r="6" fill="#ef4444"/>
                <circle cx="40" cy="20" r="6" fill="#fbbf24"/>
                <circle cx="60" cy="20" r="6" fill="#22c55e"/>
                <rect x="100" y="12" width="300" height="16" rx="8" fill="#e5e7eb"/>
                <text x="120" y="23" fill="#9ca3af" fontSize="10">app.idemenu.com/dashboard</text>
                
                {/* Sidebar */}
                <rect x="0" y="40" width="220" height="560" fill="#1f2937"/>
                
                {/* Logo */}
                <rect x="20" y="60" width="100" height="24" rx="4" fill="#7c3aed"/>
                <text x="35" y="77" fill="white" fontSize="12" fontWeight="bold">idemenu</text>
                
                {/* Nav Items */}
                <rect x="16" y="110" width="188" height="40" rx="8" fill="#7c3aed"/>
                <text x="50" y="135" fill="white" fontSize="11">📋 Menü</text>
                
                <text x="50" y="175" fill="#9ca3af" fontSize="11">📂 Kategoriler</text>
                <text x="50" y="210" fill="#9ca3af" fontSize="11">🖼️ Görseller</text>
                <text x="50" y="245" fill="#9ca3af" fontSize="11">🎨 Özelleştirme</text>
                <text x="50" y="280" fill="#9ca3af" fontSize="11">📊 İstatistikler</text>
                <text x="50" y="315" fill="#9ca3af" fontSize="11">⚙️ Ayarlar</text>
                
                {/* User */}
                <circle cx="40" cy="570" r="16" fill="#4b5563"/>
                <text x="65" y="575" fill="#d1d5db" fontSize="10">Bella Cafe</text>
                
                {/* Main Content */}
                <rect x="220" y="40" width="980" height="560" fill="#f9fafb"/>
                
                {/* Header */}
                <rect x="240" y="60" width="940" height="60" fill="white" rx="8"/>
                <text x="260" y="98" fill="#1f2937" fontSize="18" fontWeight="bold">Menü Yönetimi</text>
                <rect x="1020" y="75" width="140" height="32" rx="6" fill="#7c3aed"/>
                <text x="1055" y="96" fill="white" fontSize="11">+ Yeni Ürün</text>
                
                {/* Categories Tabs */}
                <rect x="260" y="140" width="80" height="32" rx="16" fill="#7c3aed"/>
                <text x="278" y="161" fill="white" fontSize="10">☕ Kahveler</text>
                <rect x="350" y="140" width="70" height="32" rx="16" fill="#e5e7eb"/>
                <text x="365" y="161" fill="#6b7280" fontSize="10">🥐 Tatlılar</text>
                <rect x="430" y="140" width="70" height="32" rx="16" fill="#e5e7eb"/>
                <text x="448" y="161" fill="#6b7280" fontSize="10">🥗 Salata</text>
                <rect x="510" y="140" width="80" height="32" rx="16" fill="#e5e7eb"/>
                <text x="525" y="161" fill="#6b7280" fontSize="10">🍔 Ana Yemek</text>
                
                {/* Menu Items Grid */}
                {/* Item 1 */}
                <rect x="260" y="190" width="290" height="120" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
                <rect x="280" y="210" width="80" height="80" rx="8" fill="#fef3c7"/>
                <text x="305" y="260" fontSize="30">☕</text>
                <text x="380" y="230" fill="#1f2937" fontSize="14" fontWeight="bold">Espresso</text>
                <text x="380" y="250" fill="#6b7280" fontSize="11">Klasik İtalyan espresso</text>
                <text x="380" y="280" fill="#7c3aed" fontSize="14" fontWeight="bold">₺35</text>
                <circle cx="530" cy="240" r="12" fill="#dcfce7"/>
                <text x="526" y="244" fill="#22c55e" fontSize="10">✓</text>
                
                {/* Item 2 */}
                <rect x="570" y="190" width="290" height="120" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
                <rect x="590" y="210" width="80" height="80" rx="8" fill="#fef3c7"/>
                <text x="615" y="260" fontSize="30">☕</text>
                <text x="690" y="230" fill="#1f2937" fontSize="14" fontWeight="bold">Latte</text>
                <text x="690" y="250" fill="#6b7280" fontSize="11">Sütlü espresso</text>
                <text x="690" y="280" fill="#7c3aed" fontSize="14" fontWeight="bold">₺45</text>
                <circle cx="840" cy="240" r="12" fill="#dcfce7"/>
                <text x="836" y="244" fill="#22c55e" fontSize="10">✓</text>
                
                {/* Item 3 */}
                <rect x="880" y="190" width="290" height="120" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
                <rect x="900" y="210" width="80" height="80" rx="8" fill="#fef3c7"/>
                <text x="925" y="260" fontSize="30">☕</text>
                <text x="1000" y="230" fill="#1f2937" fontSize="14" fontWeight="bold">Cappuccino</text>
                <text x="1000" y="250" fill="#6b7280" fontSize="11">Köpüklü kahve</text>
                <text x="1000" y="280" fill="#7c3aed" fontSize="14" fontWeight="bold">₺48</text>
                <circle cx="1150" cy="240" r="12" fill="#dcfce7"/>
                <text x="1146" y="244" fill="#22c55e" fontSize="10">✓</text>
                
                {/* Item 4 */}
                <rect x="260" y="330" width="290" height="120" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
                <rect x="280" y="350" width="80" height="80" rx="8" fill="#dbeafe"/>
                <text x="305" y="400" fontSize="30">🧊</text>
                <text x="380" y="370" fill="#1f2937" fontSize="14" fontWeight="bold">Iced Latte</text>
                <text x="380" y="390" fill="#6b7280" fontSize="11">Buzlu sütlü kahve</text>
                <text x="380" y="420" fill="#7c3aed" fontSize="14" fontWeight="bold">₺50</text>
                <circle cx="530" cy="380" r="12" fill="#dcfce7"/>
                <text x="526" y="384" fill="#22c55e" fontSize="10">✓</text>
                
                {/* Item 5 */}
                <rect x="570" y="330" width="290" height="120" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
                <rect x="590" y="350" width="80" height="80" rx="8" fill="#fce7f3"/>
                <text x="615" y="400" fontSize="30">🍵</text>
                <text x="690" y="370" fill="#1f2937" fontSize="14" fontWeight="bold">Mocha</text>
                <text x="690" y="390" fill="#6b7280" fontSize="11">Çikolatalı kahve</text>
                <text x="690" y="420" fill="#7c3aed" fontSize="14" fontWeight="bold">₺52</text>
                <circle cx="840" cy="380" r="12" fill="#fef3c7"/>
                <text x="836" y="384" fill="#f59e0b" fontSize="10">⏸</text>
                
                {/* Item 6 - Add New */}
                <rect x="880" y="330" width="290" height="120" rx="12" fill="white" stroke="#c4b5fd" strokeWidth="2" strokeDasharray="8 4"/>
                <circle cx="1025" cy="390" r="24" fill="#ede9fe"/>
                <text x="1017" y="398" fill="#7c3aed" fontSize="20" fontWeight="bold">+</text>
                <text x="985" y="440" fill="#7c3aed" fontSize="11">Yeni Ürün Ekle</text>
                
                {/* Stats Bar */}
                <rect x="260" y="470" width="900" height="80" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
                <text x="300" y="520" fill="#6b7280" fontSize="12">Toplam Ürün:</text>
                <text x="395" y="520" fill="#1f2937" fontSize="14" fontWeight="bold">24</text>
                <text x="480" y="520" fill="#6b7280" fontSize="12">Aktif:</text>
                <text x="520" y="520" fill="#22c55e" fontSize="14" fontWeight="bold">22</text>
                <text x="600" y="520" fill="#6b7280" fontSize="12">Pasif:</text>
                <text x="640" y="520" fill="#f59e0b" fontSize="14" fontWeight="bold">2</text>
                <text x="720" y="520" fill="#6b7280" fontSize="12">Bu Hafta Görüntüleme:</text>
                <text x="870" y="520" fill="#7c3aed" fontSize="14" fontWeight="bold">1,234</text>
              </svg>
            </div>
          </div>
        </section>

        {/* Testimonials Section - NEW */}
        <section id="testimonials" className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('landing.testimonials.title')}
              </h2>
            </div>
          
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Ahmet Yılmaz',
                  role: 'Kafe Sahibi',
                  initials: 'AY',
                  gradient: 'from-purple-500 to-indigo-600',
                  text: t('landing.testimonials.testimonial1')
                },
                {
                  name: 'Elif Demir',
                  role: 'Restoran Müdürü',
                  initials: 'ED',
                  gradient: 'from-pink-500 to-rose-600',
                  text: t('landing.testimonials.testimonial2')
                },
                {
                  name: 'Mehmet Kaya',
                  role: 'Otel İşletmecisi',
                  initials: 'MK',
                  gradient: 'from-amber-500 to-orange-600',
                  text: t('landing.testimonials.testimonial3')
                }
              ].map((testimonial, idx) => (
                <div key={idx} className="bg-gray-50 p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    {/* Avatar with gradient */}
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center text-white font-bold text-xl mr-4 shadow-lg`}>
                      {testimonial.initials}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[1,2,3,4,5].map((star) => (
                      <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      {/* Trust Section */}
      <section id="trust" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {t('landing.trust.title')}
            </h2>
            <p className="text-gray-600">{t('landing.trust.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { 
                icon: <Shield className="w-8 h-8 text-purple-600" />, 
                title: t('landing.trust.secureTitle'),
                desc: t('landing.trust.secureDesc')
              },
              { 
                icon: <Zap className="w-8 h-8 text-purple-600" />, 
                title: t('landing.trust.fastTitle'),
                desc: t('landing.trust.fastDesc')
              },
              { 
                icon: <Globe className="w-8 h-8 text-purple-600" />, 
                title: t('landing.trust.globalTitle'),
                desc: t('landing.trust.globalDesc')
              },
              { 
                icon: <HeadphonesIcon className="w-8 h-8 text-purple-600" />, 
                title: t('landing.trust.supportTitle'),
                desc: t('landing.trust.supportDesc')
              }
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-100 group"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-50 rounded-xl mb-4 group-hover:bg-purple-100 transition-colors">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="py-20 lg:py-32 bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-xl sm:text-2xl text-purple-100 mb-8 whitespace-pre-line">
            {t('landing.cta.subtitle')}
          </p>
          <button 
            onClick={() => window.location.href = '/signup'}
            className="px-12 py-4 bg-white text-purple-700 font-bold text-lg rounded-lg hover:bg-purple-50 transition-all transform hover:scale-105 shadow-xl"
          >
            {t('landing.cta.button')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <img src="/idemenu-footer-logo.svg" alt="idemenu" className="h-10 w-auto mb-4 brightness-0 invert" />
              <p className="text-gray-400">
                {t('footer.tagline')}
              </p>
            </div>
            
            {/* Ürün */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2">
                <li><a href="/features" className="hover:text-white transition-colors">{t('footer.features')}</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">{t('footer.pricing')}</a></li>
              </ul>
            </div>
            
            {/* Şirket */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2">
                <li><a href="/about" className="hover:text-white transition-colors">{t('footer.about')}</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">{t('footer.contact')}</a></li>
              </ul>
            </div>
            
            {/* Hukuki */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2">
                <li><a href="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</a></li>
                <li><a href="/kvkk" className="hover:text-white transition-colors">{t('footer.kvkk')}</a></li>
                <li><a href="/cookies" className="hover:text-white transition-colors">{t('footer.cookies')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 text-gray-500">
            <p>{t('footer.copyright')}</p>
            <span className="hidden sm:inline text-gray-700">·</span>
            <a
              href="https://idepark.com/studio.html"
              aria-label={t('footer.studioMarkAria')}
              className="text-[12px] text-gray-600 hover:text-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 rounded"
            >
              {t('footer.studioMark')}
            </a>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
